import React from 'react';
import { CartFooterProps, cn, ReserveButtonStages, SeverityLevelsEnum, TMessageBarSeverity, TTagSeverity } from 'types';
import { FactoryOrderSection, formatDate } from './sub-parts/FactoryOrderSection';
import { Button } from '../button';
import ReserveButton from './sub-parts/ReserveButton';
import FactoryOrderButton from './sub-parts/FactoryOrderButton';
import LocationFilter from './sub-parts/LocationFilter';
import { StatCard } from '../stat-card';
import { Tag } from '../tags';
import { MessageBar } from '../message-bar';
import { OrderCreationType } from 'next-common';

export function CartFooter(props: CartFooterProps) {
  const [isFactoryOrderOpen, setIsFactoryOrderOpen] = React.useState<boolean>(false);

  const isVehicleReserved = props.reserveButtonStage === ReserveButtonStages.RESERVED;

  const isReservationInWaiting =
    props.reserveButtonStage === ReserveButtonStages.WAITING || props.isIFSReservationInProgress;

  const validOrderTypesForRemoveButton = [OrderCreationType.DEMO, OrderCreationType.STOCK];
  const canRenderActionButtons =
    (!!props.serialNo && props.showReserveButton) ||
    props.isAllowOffer ||
    (props.vehicleCount > 0 && props.showChangeSpecificCarButton) ||
    props.isFactoryOrderEnable ||
    (!!props?.serialNo && validOrderTypesForRemoveButton?.includes(props?.orderType as OrderCreationType));

  const factoryRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    setIsFactoryOrderOpen(false);
  }, [props.isFactoryOrderEnable]);

  React.useEffect(() => {
    if (isFactoryOrderOpen) factoryRef?.current?.scrollIntoView({ behavior: 'smooth' });
  }, [isFactoryOrderOpen]);
  return (
    <div className="bg-lightGrey rounded-b-[0.5rem] flex flex-col gap-6 pt-8 px-0 pb-6">
      {props.isShowFooter ? (
        <div className={`px-6 w-full flex flex-col ${isVehicleReserved ? 'gap-5' : 'gap-6'} empty:hidden peer`}>
          {props.isVehicleSelected ? (
            <>
              <div className={cn('flex flex-col items-start self-stretch gap-2', !props.isFactoryOrder && 'gap-1')}>
                <p className="text-base font-semibold leading-[150%] text-black">
                  {props.isFactoryOrder ? 'Fabrikkbestilling, med ønsket produksjonsdato:' : 'Valgt bil'}
                </p>
                {!!props.serialNo && (
                  <div className="m-0 text-black text-2xl font-extrabold leading-[150%]">
                    {props.serialNo ? props.serialNo : props.isFactoryOrder ? 'Bestill fra fabrikk' : ''}
                  </div>
                )}
                {(props.selectedLocation || props.selectedStatus || props.vehicleTTCode !== undefined) &&
                  !props.isFactoryOrder && (
                    <div className="flex items-center">
                      {props.vehicleTTCode !== undefined && (
                        <Tag severity={TTagSeverity.NEUTRAL} className="mr-2">
                          <span className="text-sm/[170%] font-semibold">{props.vehicleTTCode}</span>
                        </Tag>
                      )}
                      <p className="text-base/[175%] font-semibold text-concrete">
                        {props.selectedStatus ?? ''}
                        {props.selectedStatus && props.selectedLocation ? ', ' : ''}
                        Lokasjon: {props.selectedLocation ?? ''}
                      </p>
                    </div>
                  )}
                {props.isFactoryOrder && (
                  <div className="m-0 text-black text-2xl font-extrabold leading-[150%]">
                    {props.isFactoryOrder ? formatDate(props.expectedProductionDate, true) : ''}
                  </div>
                )}
                {props.showAbortFactoryOrderAction && props.isFactoryOrder && (
                  <div className="grid grid-cols-2 items-center gap-4 w-full pt-8">
                    <Button
                      variant={'teal'}
                      appearance="outline"
                      onClick={props.abortFactoryBorder?.buttonAction}
                      className={'leading-[175%]'}
                    >
                      <span>{props.abortFactoryBorder?.buttonLabel}</span>
                    </Button>
                  </div>
                )}
              </div>

              {canRenderActionButtons && (
                <>
                  {props.showReservationInfo && props.reservationToDateLabel && (
                    <MessageBar className="tw-small w-full" severity={TMessageBarSeverity.INFORMATION}>
                      <span className="text-base/[175%] text-black">
                        <span className="font-semibold">Bilen er reservert til </span>
                        <span className="font-extrabold">{props.reservationToDateLabel}</span>
                      </span>
                    </MessageBar>
                  )}
                  <div className={`flex flex-wrap gap-8 ${!props.isFactoryOrder && !isVehicleReserved ? 'py-4' : ''}`}>
                    <div
                      className={`grid ${isVehicleReserved ? 'grid-cols-1' : 'grid-cols-2'} items-center gap-4 w-full`}
                    >
                      {!!props.serialNo && !!props.showReserveButton && (
                        <ReserveButton
                          isVehicleReserved={isVehicleReserved}
                          isReservationInWaiting={isReservationInWaiting}
                          isSelectedVehicleUnavailable={props.isSelectedVehicleUnavailable}
                          onReserveVehicle={props.onReserveVehicle}
                          isLoading={props.isLoading}
                          reservationInfoText={props.reservationInfoText}
                        />
                      )}
                      {props.showAbortFactoryOrderAction && !props.isFactoryOrder && (
                        <Button
                          variant={'teal'}
                          appearance="outline"
                          onClick={props.abortFactoryBorder?.buttonAction}
                          className={'flex w-full justify-center items-center'}
                        >
                          <span>Fjern bil</span>
                        </Button>
                      )}
                      {props.isAllowOffer && (
                        <Button className="w-full items-center" onClick={props.onSaveOfferPDF}>
                          <span>Gi tilbud</span>
                        </Button>
                      )}
                      {props.vehicleCount > 0 && props.showChangeSpecificCarButton && (
                        <Button
                          className="w-full items-center"
                          onClick={() => props.onOpenVehicleList()}
                          disabled={isVehicleReserved || isReservationInWaiting}
                          variant={props.serialNo || props.isFactoryOrder ? 'teal' : 'purple'}
                        >
                          <span className="text-base/[175%] font-extrabold">
                            {props.serialNo || props.isFactoryOrder ? 'Bytt valgt bil' : 'Se alle biler på lager'}{' '}
                          </span>
                        </Button>
                      )}

                      {props.isFactoryOrderEnable && (
                        <FactoryOrderButton
                          isFactoryOrderOpen={isFactoryOrderOpen}
                          isReservationInWaiting={isReservationInWaiting}
                          setIsFactoryOrderOpen={setIsFactoryOrderOpen}
                          isVehicleReserved={isVehicleReserved}
                        />
                      )}
                    </div>
                  </div>
                </>
              )}
            </>
          ) : (
            <>
              <div className={cn('flex flex-col gap-6', { disableAccordian: !props.isFilterAllow })}>
                {props?.isFilterAllow && (
                  <div className="p-0 grid grid-cols-2 gap-6">
                    <StatCard
                      title={'biler på lager med denne konfigurasjonen'}
                      statCount={props?.exactMatchCount || 0}
                      severityLevel={SeverityLevelsEnum.ERROR}
                    />
                    <StatCard
                      title={'biler på lager med denne konfigurasjonen + tillegg'}
                      statCount={props?.nonExactMatchCount || 0}
                      severityLevel={SeverityLevelsEnum.SUCCESS}
                    />
                  </div>
                )}
                {props?.isFilterAllow && (
                  <LocationFilter
                    location={props.location}
                    onSelectLocation={props.onSelectLocation}
                    vehicleCountBySeller={props.vehicleCountBySeller}
                    onOpenVehicleList={props.onOpenVehicleList}
                    onSellerChangeByCount={props.onSellerChangeByCount}
                  />
                )}
              </div>
              {((props.vehicleCount > 0 && props.showChangeSpecificCarButton) || props.isFactoryOrderEnable) && (
                <div className="flex flex-wrap gap-8">
                  <div className="grid grid-cols-2 items-center gap-4 w-full">
                    {props.vehicleCount > 0 && props.showChangeSpecificCarButton && (
                      <Button
                        onClick={() => {
                          props.onOpenVehicleList();
                        }}
                        className="w-full items-center"
                        disabled={isVehicleReserved}
                        variant="teal"
                      >
                        <span className="text-base/[175%] font-extrabold">
                          {props.serialNo || props.isFactoryOrder ? 'Bytt valgt bil' : 'Se alle biler på lager'}{' '}
                        </span>
                      </Button>
                    )}
                    {props.isFactoryOrderEnable && (
                      <FactoryOrderButton
                        isFactoryOrderOpen={isFactoryOrderOpen}
                        isReservationInWaiting={isReservationInWaiting}
                        setIsFactoryOrderOpen={setIsFactoryOrderOpen}
                        isVehicleReserved={isVehicleReserved}
                        isDisabled={props.isFactoryOrderButtonDisabled}
                      />
                    )}
                  </div>
                </div>
              )}
            </>
          )}
          {isFactoryOrderOpen && props.isFactoryOrderEnable && (
            <FactoryOrderSection
              onSaveFactoryOrder={props.onSaveFactoryOrder}
              setIsFactoryOrderOpen={setIsFactoryOrderOpen}
              expectedProductionDate={props.expectedProductionDate}
              ref={factoryRef}
              isLoading={props.isLoading}
              isProdDateDisabled={props.isProdDateDisabled}
            />
          )}
        </div>
      ) : (
        <p className="px-6">Ingen lagerstatus</p>
      )}
      {props.footerChildren}
    </div>
  );
}
